import {
  colorContentPlaceholder,
  colorContentSubtle,
  colorStatusPositiveDefault,
} from '@duxton-web/design-tokens/themes/light';
import { MoreVerticalUniversal, TickMarkOutlined } from '@duxton-web/icons';
import { Card, Col, Dropdown, Flex, Typography } from '@duxton-web/ui';
import _ from 'lodash';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import {
  INTERNAL_STATUS,
  QUERY_PARAM_KEYS,
  UCD_RESOURCE_FIELDS,
  UCD_ROUTES_LINK,
} from 'omega-rtc/constants/ucd';
import { getCountryNameFromCountryID } from 'omega-rtc/utils/ucd-utils';
import { hasPermission } from 'omega-rtc/utils/user-utils';
import ProjectStatusTag from 'omega-rtc/views/ucd/ProjectStatusTag';
import ShowIfAuthorized from './common/ShowIfAuthorized';
import './ProjectCard.scss';


type ProjectActions = {
  options: [
    { displayName: string, value: string, icon: string, callback: Function, condition: { resource: string, action: string } },
  ],
};

type Props = {
  project: Object,
  width: string,
  height: string,
  actions: ProjectActions,
};

const ProjectCard = (props: Props) => {
  const { project, status, projectID, campaignCount } = props.project;
  const userPermissions = useSelector((state) => state.app.user.userPermissions);
  const countries = useSelector((state) => state.countries.entities);


  const { name, countryID } = project;

  const filterInvalidActions = (actions) => {
    return _.filter(actions, (action) => {
      if (
        action.key === 'add_campaign' &&
        status === INTERNAL_STATUS.deactivated
      ) {
        return false;
      }

      return hasPermission(
        userPermissions,
        action.condition.resource,
        UCD_RESOURCE_FIELDS.all,
        action.condition.action,
      );
    });
  };
  const projectDetailPageURL = `${UCD_ROUTES_LINK.PROJECT_VIEW(projectID)}?${QUERY_PARAM_KEYS.previousPageIsListing}=true`;

  const cardExtras = useMemo(() => {
    const validActions = filterInvalidActions(props.actions.options);

    return (
      validActions.length > 0 && (
        <ShowIfAuthorized
          conditions={_.map(
            props.actions.options,
            (action) => action.condition,
          )}
        >
          <Dropdown
            menu={{
              items: validActions,
              onClick: (e) => {
                validActions.find((action) => action.key === e.key).callback(props.project);
              },
            }}
            trigger={['click']}
          >
            <MoreVerticalUniversal />
          </Dropdown>
        </ShowIfAuthorized>
      )
    );
  }, [props.actions, props.project]);

  return (
    <Col span={8}>
      <Card
        styles={{
          body: { paddingTop: 0 },
          header: { borderBottom: 0, minHeight: 'auto', marginBottom: 'auto' },
        }}
        style={{
          height: props.height,
          paddingTop: 20,
          paddingBottom: 20,
        }}
        title={name}
        extra={cardExtras}
      >
        <Link to={projectDetailPageURL}>
          <Flex gap={16} vertical={false}>
            <Typography.Metadata style={{ color: colorContentSubtle }}>
              {getCountryNameFromCountryID(countryID, countries)}
            </Typography.Metadata>
            <Typography.Metadata style={{ color: colorContentSubtle }}>
              {campaignCount || 0} Campaigns
            </Typography.Metadata>
          </Flex>

          <Flex gap={4} vertical={false} justify="flex-start" align="center" style={{ paddingTop: 32 }}>
            <ProjectStatusTag status={status} />
            <TickMarkOutlined
              style={{
                color: _.get(props.project, 'isMetricsAvailable')
                  ? colorStatusPositiveDefault
                  : colorContentPlaceholder,
                fontSize: 24,
              }}
            />
          </Flex>
        </Link>
      </Card>
    </Col>
  );
};

export default ProjectCard;
